import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import TextAlign from "@tiptap/extension-text-align"
import {
  TextStyle,
  FontFamily,
  FontSize,
  LineHeight,
} from "@tiptap/extension-text-style"
import { useEffect, useRef, useState } from "react"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  Type,
} from "lucide-react"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------
// Options for the dropdowns
// ---------------------------------------------------------------

const FONT_FAMILIES: { label: string; value: string }[] = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Tahoma", value: "Tahoma, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: '"Times New Roman", Times, serif' },
  { label: "Courier New", value: '"Courier New", Courier, monospace' },
]

const FONT_SIZES = [
  { label: "10", value: "10px" },
  { label: "12", value: "12px" },
  { label: "14", value: "14px" },
  { label: "16", value: "16px" },
  { label: "18", value: "18px" },
  { label: "20", value: "20px" },
  { label: "24", value: "24px" },
  { label: "28", value: "28px" },
  { label: "32", value: "32px" },
  { label: "36", value: "36px" },
  { label: "48", value: "48px" },
]

const LINE_HEIGHTS = [
  // NB: line-height: 1 renders cramped in email clients (Outlook, Gmail).
  // "Single" here uses 1.2, which matches browser default and still looks
  // like standard single-spacing to a user. If you REALLY need tight (1.0)
  // use custom CSS.
  { label: "Single", value: "1.2" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "Double", value: "2" },
]

// ---------------------------------------------------------------
// Component
// ---------------------------------------------------------------

export type RichTextEditorProps = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  className?: string
  onFocus?: () => void
  onBlur?: () => void
  /** Called when the user picks an image from disk. Return the URL to insert. */
  onImageUpload?: (file: File) => Promise<string>
}

/**
 * WYSIWYG rich text editor for automation email bodies and signatures.
 * Emits HTML on change (sanitized on the server before send).
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 200,
  className,
  onFocus,
  onBlur,
  onImageUpload,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      LineHeight,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Image.configure({
        inline: false,
        // Accept data: URLs so pasted / uploaded images survive a save/reload
        // round-trip (they're stored inline in the signature HTML).
        allowBase64: true,
        HTMLAttributes: { style: "max-width: 100%; height: auto;" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none px-3 py-2",
          "prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1"
        ),
        style: `min-height: ${minHeight}px`,
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    onFocus,
    onBlur,
  })

  // Keep editor in sync when value changes externally
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if (value !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor])

  if (!editor) return null

  return (
    <div
      className={cn(
        "border-border bg-white rounded-md border focus-within:ring-2 focus-within:ring-rc-blue-500 focus-within:ring-offset-1",
        className
      )}
    >
      <Toolbar editor={editor} onImageUpload={onImageUpload} />
      <EditorContent editor={editor} className="max-h-[600px] overflow-y-auto" />
      {!value && placeholder && !editor.isFocused && (
        <div
          className="pointer-events-none absolute px-3 py-2 text-sm text-muted-foreground"
          style={{ marginTop: -minHeight + 8 }}
        >
          {placeholder}
        </div>
      )}
    </div>
  )
}

function Toolbar({
  editor,
  onImageUpload,
}: {
  editor: Editor
  onImageUpload?: (file: File) => Promise<string>
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const insertLink = () => {
    const current = editor.getAttributes("link").href as string | undefined
    setLinkUrl(current || "")
    setLinkOpen(true)
  }

  const commitLink = () => {
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run()
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkUrl.startsWith("http") ? linkUrl : `https://${linkUrl}` })
        .run()
    }
    setLinkOpen(false)
  }

  const pickImage = () => fileInputRef.current?.click()

  const handleImageChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ""
    setUploadError(null)
    setUploading(true)
    try {
      let url: string
      if (onImageUpload) {
        url = await onImageUpload(file)
      } else {
        // Fallback: embed as base64 data URL. Fine for tiny signature images
        // only — anything above ~50KB will bloat the HTML dramatically.
        if (file.size > 100 * 1024) {
          throw new Error(
            "Image too large. Use a smaller image or an image URL."
          )
        }
        url = await new Promise<string>((resolve, reject) => {
          const r = new FileReader()
          r.onload = () => resolve(r.result as string)
          r.onerror = reject
          r.readAsDataURL(file)
        })
      }
      editor.chain().focus().setImage({ src: url }).run()
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Image upload failed"
      setUploadError(msg)
      console.error("Image upload failed", err)
    } finally {
      setUploading(false)
    }
  }

  // Derive current font/size/line-height for select values.
  const currentFontFamily =
    (editor.getAttributes("textStyle").fontFamily as string | undefined) ?? ""
  const currentFontSize =
    (editor.getAttributes("textStyle").fontSize as string | undefined) ?? ""
  const currentLineHeight =
    (editor.getAttributes("paragraph").lineHeight as string | undefined) ??
    (editor.getAttributes("heading").lineHeight as string | undefined) ??
    ""

  return (
    <div className="border-b border-border flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-rc-grey-50/50">
      {/* Font family */}
      <SelectControl
        title="Font"
        value={currentFontFamily}
        onChange={(v) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const c = editor.chain().focus() as any
          if (!v) {
            c.unsetFontFamily().run()
          } else {
            c.setFontFamily(v).run()
          }
        }}
        options={FONT_FAMILIES}
        widthClass="w-28"
      />

      {/* Font size */}
      <SelectControl
        title="Font size"
        value={currentFontSize}
        onChange={(v) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const c = editor.chain().focus() as any
          c.setFontSize(v || null).run()
        }}
        options={[{ label: "Size", value: "" }, ...FONT_SIZES]}
        widthClass="w-16"
      />

      {/* Line height */}
      <SelectControl
        title="Line spacing"
        value={currentLineHeight}
        onChange={(v) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const c = editor.chain().focus() as any
          c.setLineHeight(v || null).run()
        }}
        options={[{ label: "Spacing", value: "" }, ...LINE_HEIGHTS]}
        widthClass="w-20"
      />

      <Divider />

      <ToolButton
        title="Bold (Cmd+B)"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Italic (Cmd+I)"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Underline (Cmd+U)"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </ToolButton>

      <Divider />

      <ToolButton
        title="Heading 1"
        active={editor.isActive("heading", { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Heading 3"
        active={editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Paragraph"
        active={editor.isActive("paragraph")}
        onClick={() => editor.chain().focus().setParagraph().run()}
      >
        <Type className="h-3.5 w-3.5" />
      </ToolButton>

      <Divider />

      <ToolButton
        title="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolButton>

      <Divider />

      <ToolButton
        title="Align left"
        active={editor.isActive({ textAlign: "left" })}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Align center"
        active={editor.isActive({ textAlign: "center" })}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
      >
        <AlignCenter className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Align right"
        active={editor.isActive({ textAlign: "right" })}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
      >
        <AlignRight className="h-3.5 w-3.5" />
      </ToolButton>

      <Divider />

      <div className="relative">
        <ToolButton
          title="Add link"
          active={editor.isActive("link")}
          onClick={insertLink}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolButton>
        {linkOpen && (
          <div className="border-border absolute left-0 top-full z-30 mt-1 flex items-center gap-1 rounded-md border bg-white p-2 shadow-lg">
            <input
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitLink()
                if (e.key === "Escape") setLinkOpen(false)
              }}
              placeholder="https://example.com"
              className="border-border h-7 w-56 rounded border px-2 text-xs"
            />
            <button
              onClick={commitLink}
              className="h-7 rounded bg-rc-blue-600 px-2 text-xs text-white hover:bg-rc-blue-700"
            >
              Set
            </button>
            <button
              onClick={() => setLinkOpen(false)}
              className="h-7 rounded px-2 text-xs text-muted-foreground hover:bg-rc-grey-100"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <ToolButton
        title={uploading ? "Uploading…" : "Insert image"}
        onClick={pickImage}
        disabled={uploading}
      >
        <ImageIcon className="h-3.5 w-3.5" />
      </ToolButton>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={handleImageChosen}
      />
      {uploadError && (
        <span className="ml-2 text-[11px] text-red-600" title={uploadError}>
          Image upload failed
        </span>
      )}

      <Divider />

      <ToolButton
        title="Undo (Cmd+Z)"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton
        title="Redo (Cmd+Shift+Z)"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-3.5 w-3.5" />
      </ToolButton>
    </div>
  )
}

function ToolButton({
  active,
  disabled,
  onClick,
  title,
  children,
}: {
  active?: boolean
  disabled?: boolean
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded transition-colors",
        active
          ? "bg-rc-blue-100 text-rc-blue-700"
          : "text-muted-foreground hover:bg-rc-grey-100 hover:text-foreground",
        disabled && "cursor-not-allowed opacity-40 hover:bg-transparent"
      )}
    >
      {children}
    </button>
  )
}

function SelectControl({
  title,
  value,
  onChange,
  options,
  widthClass,
}: {
  title: string
  value: string
  onChange: (v: string) => void
  options: { label: string; value: string }[]
  widthClass?: string
}) {
  return (
    <select
      title={title}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "h-7 rounded border border-border bg-white px-1.5 text-xs text-foreground",
        "focus:outline-none focus:ring-1 focus:ring-rc-blue-500",
        widthClass ?? "w-24"
      )}
    >
      {options.map((o) => (
        <option key={o.value || o.label} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function Divider() {
  return <span className="mx-1 h-4 w-px bg-border" />
}
